import { Component, Input, ChangeDetectionStrategy } from '@angular/core';
import { Router } from '@angular/router';
import { Location } from '@angular/common';

interface ArticleAuthor {
  id?: number;
  name?: string;
  nameFirst?: string;
  nameLast?: string;
  title?: string;
  orcid?: string;
  email?: string;
  institutionName?: string;
  institutionAddress?: string;
  country?: string;
  websiteUrl?: string;
}

interface ArticleData {
  id?: number;
  ID?: number;  // Backend ile uyumluluk için
  slug?: string;
  title?: string;
  abstract?: string;
  doi?: string;
  journalSlug?: string;
  section?: string;
  localeText?: string;
  startPage?: number;
  endPage?: number;
  authors?: ArticleAuthor[];
  publishedDate?: string;
  volume?: number;
  issue?: number;
  keywords?: string[];
  status?: string;
}

@Component({
    selector: 'app-article-view',
    templateUrl: './article-view.component.html',
    styleUrls: ['./article-view.component.css'],
    changeDetection: ChangeDetectionStrategy.Eager,
    standalone: false
})
export class ArticleViewComponent {
  @Input() dataList: ArticleData[] = [];
  @Input() count: number = 10;
  @Input() col: number = 1;
  @Input() parentSlug?: string;

  constructor(private router: Router, private location: Location) {
   }

  ngOnInit() {
    // Component initialization logic if needed
  }

  /**
   * Get the articles to display based on the count limit
   */
  getDisplayArticles(): ArticleData[] {
    return this.dataList.slice(0, this.count);
  }

  /**
   * Get the column class for Bootstrap grid
   */
  getColumnClass(): string {
    const colSize = 12 / (this.col || 1);
    return `col-md-${colSize}`;
  }

  /**
   * Navigate to article detail page
   */
  navigateToArticle(article: ArticleData) {
    const articleId = article.ID || article.id;  // Backend ID veya local id kullan
    const queryParams = articleId ? { id: articleId } : {};
    
    if (article.slug && article.journalSlug) {
      this.router.navigate(['/journal', article.journalSlug, 'article', article.slug], { queryParams });
    }
    else if (this.parentSlug) {
      this.router.navigate(['/journal', this.parentSlug, 'article', article.slug], { queryParams });
    }
  }

  /**
   * Navigate to article with browser history support
   */
  navigateToArticleWithHistory(article: ArticleData) {
    let url = '';
    if (article.slug && article.journalSlug) {
      url = `/journal/${article.journalSlug}/article/${article.slug}`;
    }
    else if (this.parentSlug && article.slug) {
      url = `/journal/${this.parentSlug}/article/${article.slug}`;
    }
    
    if (url) {
      // Add query parameter if ID exists
      const articleId = article.ID || article.id;
      if (articleId) {
        url += `?id=${articleId}`;
      }
      // Use location.go to change URL without triggering navigation
      this.location.go(url);
      // Then navigate programmatically
      this.router.navigateByUrl(url);
    }
  }

  /**
   * Get article route for routerLink
   */
  getArticleRoute(article: ArticleData): string[] {
    if (article.slug && article.journalSlug) {
      return ['/journal', article.journalSlug, 'article', article.slug];
    }
    else if (this.parentSlug && article.slug) {
      return ['/journal', this.parentSlug, 'article', article.slug];
    }
    return [];
  }

  /**
   * Get article URL for href
   */
  getArticleUrl(article: ArticleData): string {
    if (article.slug && article.journalSlug) {
      return `/journal/${article.journalSlug}/article/${article.slug}`;
    }
    else if (this.parentSlug && article.slug) {
      return `/journal/${this.parentSlug}/article/${article.slug}`;
    }
    return '#';
  }

  /**
   * Get formatted abstract with proper HTML ending
   */
  getFormattedAbstract(abstract: string): string {
    if (!abstract) return '';
    
    let formattedAbstract = abstract + '...';
    
    // Add proper closing tag based on the opening tag
    if (abstract.indexOf('<div>') === 0) {
      formattedAbstract += '</div>';
    } else {
      formattedAbstract += '</p>';
    }
    
    return `<div class="from-journal-content text-justify">${formattedAbstract}</div>`;
  }

  /**
   * Get author display name
   */
  getAuthorDisplayName(author: ArticleAuthor): string {
    if (author.nameFirst && author.nameLast) {
      return `${author.nameFirst} ${author.nameLast}`;
    }
    return author.name || 'Unknown Author';
  }

  /**
   * Get language code from locale text
   */
  getLanguageCode(localeText: string): string {
    if (!localeText) return '';
    return localeText.split('_')[0].toUpperCase();
  }

  /**
   * Check if article has abstract
   */
  hasAbstract(article: ArticleData): boolean {
    return !!(article.abstract && article.abstract.trim());
  }

  /**
   * Check if article has DOI
   */
  hasDoi(article: ArticleData): boolean {
    return !!(article.doi && article.doi.trim());
  }

  /**
   * Get DOI URL
   */
  getDoiUrl(doi: string): string {
    if (!doi) return '';
    return `https://dx.doi.org/${doi}`;
  }

  /**
   * Check if article has page numbers
   */
  hasPageNumbers(article: ArticleData): boolean {
    return !!(article.startPage && article.endPage);
  }

  /**
   * Check if this is the last item in the list (for HR separator)
   */
  isLastItem(index: number): boolean {
    return index >= (this.count - 1);
  }

  /**
   * Get formatted page range
   */
  getPageRange(article: ArticleData): string {
    if (!this.hasPageNumbers(article)) return '';
    return `${article.startPage}-${article.endPage}`;
  }
}
